<template>
  <div>
    <div v-if="!loaded" class="preloader-overlay">
      <div class="preloader-card">
        <f7-preloader></f7-preloader>
        <div class="preloader-text">
          {{ $t('loading') }}
        </div>
      </div>
    </div>
    <div class="summary-header">
      <div class="summary-title">
        <div class="summary-icon-invoices"></div>
        <div class="summary-text">{{ $t('myIssuedInvoices') }}</div>
      </div>
      <div class="summary-header-actions">
        <a
          id="issued-invoices-info-header"
          data-tour-id="issued-invoices-info-button"
          class="issued-invoices-info issued-invoices-info--header"
          @mouseenter="toggleWithAnimationOptions"
          @mouseleave="toggleWithAnimationOptions"
        >
          <i class="f7-icons f7-icon-custom">info_circle_fill</i>
        </a>
      </div>
    </div>
    <div data-tour-id="issued-invoices-grid">
      <DxDataGrid
        :data-source="filteredStore"
        :show-borders="false"
        :show-column-lines="false"
        :allow-column-reordering="true"
        :allow-column-resizing="true"
        :column-auto-width="true"
        :show-column-headers="true"
        :show-colon="false"
        :remote-operations="true"
        :load-panel="{ enabled: false }"
        :hover-state-enabled="false"
        :show-row-lines="false"
        :row-alternation-enabled="false"
        :no-data-text="$t('noDataText')"
        class="custom-grid custom-grid-summary"
        @content-ready="init"
        @toolbar-preparing="onToolbarPreparing($event)"
      >
        <DxScrolling mode="standard" />

        <DxStateStoring
          :enabled="true"
          type="custom"
          storage-key="IssuedInvoicesTable"
          :custom-load="loadState"
          :custom-save="customSave"
        />

        <DxPaging :page-size="10" />

        <DxPager
          :show-page-size-selector="true"
          :allowed-page-sizes="[5, 10, 20]"
          :show-info="true"
          info-text="{2} items"
          :visible="true"
        />

        <DxSearchPanel
          :visible="true"
          :width="300"
          :placeholder="$t('search')"
        />

        <!-- Código de factura -->
        <DxColumn
          data-field="CodFactura"
          :caption="$t('issuedInvoiceCode')"
          :allow-sorting="true"
          :fixed="true"
          :min-width="120"
          fixed-position="left"
          :allow-header-filtering="false"
        />

        <!-- Fecha de emisión -->
        <DxColumn
          data-field="FechaFacturaDate"
          :caption="$t('issuedInvoiceDate')"
          :allow-sorting="true"
          :fixed="true"
          alignment="center"
          :min-width="120"
          :allow-header-filtering="false"
          sort-order="desc"
          :sort-index="0"
          data-type="date"
          :format="$helpers.getDateFormat(company)"
        />

        <!-- Orden de Compra -->
        <DxColumn
          data-field="OrdenCompra"
          :caption="$t('orderCode')"
          :allow-sorting="true"
          :fixed="true"
          alignment="center"
          :min-width="200"
          :allow-header-filtering="false"
          cell-template="ordenCompraTemplate"
        />

        <!-- Importe -->
        <DxColumn
          data-field="Importe"
          :caption="$t('totalIncludingTaxes')"
          :fixed="true"
          :min-width="100"
          alignment="center"
          cell-template="importeTemplate"
        />

        <!-- Estado -->
        <DxColumn
          data-field="Estado"
          :caption="$t('SamplesList.status')"
          :fixed="true"
          alignment="center"
          :min-width="75"
          :allow-header-filtering="false"
          cell-template="cellTemplateStatus"
        />

        <!-- Acciones -->
        <DxColumn
          :buttons="updatedEditButtons"
          :fixed="true"
          fixed-position="right"
          type="buttons"
          :min-width="40"
          name="actionColumn"
        />

        <!-- Template Importe -->
        <template #importeTemplate="{ data }">
          <span>
            {{ formatAmount(data.data.Importe, data.data.Divisa) }}
          </span>
        </template>

        <!-- Template Orden de Compra -->
        <!-- Habrá que revisar el código de estado o algo similar, por si cambia con el idioma -->
        <template #ordenCompraTemplate="{ data }">
          <div
            :class="mapBackendStatus(data.data.OrdenCompra).class"
            style="max-width: 200px"
          >
            {{
              $t(mapBackendStatus(data.data.OrdenCompra || 'OK').i18n)
            }}
          </div>
        </template>

        <!-- Template Estado -->
        <template #cellTemplateStatus="{ data }">
          <div
            :class="mapBackendStatus(data.data.Estado).class"
            class="status-text"
            style="text-align: center"
          >
            {{ $t(mapBackendStatus(data.data.Estado).i18n) }}
          </div>
        </template>
      </DxDataGrid>
    </div>
    <DxPopover
      width="auto"
      height="auto"
      class="issued-invoices-popover"
      :visible="withAnimationOptionsVisible"
      :animation="animationConfig"
      target="#issued-invoices-info-header"
      position="right"
    >
      <div style="padding: 1px 15px">
        <p>{{ $t('lastSixMonthsFilterInfo') }}</p>
      </div>
    </DxPopover>
  </div>
</template>

<script>
import {
  DxDataGrid,
  DxColumn,
  DxSearchPanel,
  DxStateStoring,
  DxPager,
  DxPaging,
  DxScrolling,
} from 'devextreme-vue/data-grid';
import { mapActions, mapGetters, mapState } from 'vuex';
import { DxPopover } from 'devextreme-vue/popover';
import iconVisibility from '@/static/icons/ic_visibility.svg';
import iconDownload from '@/static/icons/ic_download.svg';

export default {
  components: {
    DxDataGrid,
    DxColumn,
    DxSearchPanel,
    DxStateStoring,
    DxPager,
    DxPaging,
    DxScrolling,
    DxPopover,
  },
  props: {
    store: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isFilterActive: {
        pending: false,
        paid: false,
      },
      editButtons: [
        {
          icon: 'find',
          onClick: this.viewAtc,
        },
      ],
      visible: true,
      loaded: false,
      withAnimationOptionsVisible: false,
      animationConfig: {
        show: {
          type: 'pop',
          from: {
            scale: 0,
          },
          to: {
            scale: 1,
          },
        },
        hide: {
          type: 'fade',
          from: 1,
          to: 0,
        },
      },
    };
  },
  computed: {
    filteredStore() {
      const invoices = Array.isArray(this.store) ? this.store : [];
      const activeStatuses = [];

      if (this.isFilterActive.pending) {
        activeStatuses.push('Pendiente');
      }

      if (this.isFilterActive.paid) {
        activeStatuses.push('Pagada');
      }

      if (!activeStatuses.length) {
        return invoices;
      }

      return invoices.filter((invoice) =>
        activeStatuses.includes(invoice?.Estado)
      );
    },
    updatedEditButtons() {
      return [
        {
          icon: iconVisibility,
          cssClass:
            'grid-button-icon issued-invoices-detail-button',
          onClick: this.viewAtc,
        },
        {
          icon: iconDownload,
          cssClass:
            'grid-button-icon issued-invoices-download-button',
          onClick: this.download,
        },
      ];
    },
    ...mapState('contact', ['company']),
    ...mapState('issuedInvoicesModule', [
      'showSplPopup',
      'currency',
      'issuedInvoiceList',
      'issuedInvoiceSelected',
    ]),
    ...mapGetters('user', ['getLanguage']),
  },
  methods: {
    saveGridState() {
      if (!this.grid?.component) return;
      this.customSave(this.grid.component.state());
    },
    toggleStatusFilter(filterKey, element) {
      this.isFilterActive[filterKey] = !this.isFilterActive[filterKey];

      if (element) {
        element.classList.toggle(
          'show-icon',
          this.isFilterActive[filterKey]
        );
        element.classList.toggle(
          'hide-icon',
          !this.isFilterActive[filterKey]
        );
      }

      this.saveGridState();
    },
    toggleWithAnimationOptions() {
      this.withAnimationOptionsVisible =
        !this.withAnimationOptionsVisible;
    },
    mapBackendStatus(value) {
      const map = {
        Pendiente: {
          class: 'orangeStatus',
          i18n: 'issuedInvoiceStatus.pending',
        },
        Pagada: {
          class: 'greenStatus',
          i18n: 'issuedInvoiceStatus.paid',
        },
        OK: {
          class: 'greyStatus',
          i18n: 'issuedInvoiceStatus.ok',
        },
        '': {
          class: 'greyStatus',
          i18n: 'issuedInvoiceStatus.ok',
        },
      };

      return (
        map[value] || {
          class: 'greyStatus',
          i18n: value,
        }
      );
    },
    loadState() {
      let state = localStorage.getItem('IssuedInvoicesTable');
      if (state) {
        state = JSON.parse(state);
        if (state.isFilterActive) {
          Object.assign(this.isFilterActive, state.isFilterActive);
          delete state.isFilterActive;
        }
        state.searchText = '';
      }
      return state;
    },
    customSave(state) {
      localStorage.setItem(
        'IssuedInvoicesTable',
        JSON.stringify({
          ...state,
          isFilterActive: this.isFilterActive,
        })
      );
    },
    init(e) {
      this.grid = e;
      this.loaded = true;
    },
    onToolbarPreparing(e) {
      const toolbarItems = e.toolbarOptions.items;
      e.toolbarOptions.elementAttr = {
        class: 'custom-simple-toolbar issued-invoices-toolbar',
        'data-tour-id': 'issued-invoices-toolbar',
      };
      toolbarItems.forEach((item) => {
        if (item.name === 'searchPanel') {
          item.location = 'before';
        }
      });

      toolbarItems.push({
        name: 'Filtros',
        location: 'before',
        widget: 'dxButton',
        locateInMenu: 'never',
        options: {
          text: this.$t('filter'),
          icon: '././static/icons/ic_filter.svg',
          width: 115,
          elementAttr: {
            class: 'btn-filtrar',
            'data-tour-id': 'issued-invoices-filter-button',
          },
          onClick: () => {
            this.$f7.panel.open('right');
          },
        },
      });

      toolbarItems.push({
        name: 'IssuedInvoicesPending',
        location: 'before',
        widget: 'dxButton',
        locateInMenu: 'never',
        options: {
          text: this.$t('issuedInvoiceStatus.pending'),
          icon: '././static/icons/ic_check.svg',
          width: 150,
          elementAttr: {
            class: `filtrosBotones ${
              this.isFilterActive.pending
                ? 'show-icon'
                : 'hide-icon'
            }`,
          },
          onClick: (args) => {
            this.toggleStatusFilter('pending', args.element);
          },
        },
      });

      toolbarItems.push({
        name: 'IssuedInvoicesPaid',
        location: 'before',
        widget: 'dxButton',
        locateInMenu: 'never',
        options: {
          text: this.$t('issuedInvoiceStatus.paid'),
          icon: '././static/icons/ic_check.svg',
          width: 150,
          elementAttr: {
            class: `filtrosBotones ${
              this.isFilterActive.paid ? 'show-icon' : 'hide-icon'
            }`,
          },
          onClick: (args) => {
            this.toggleStatusFilter('paid', args.element);
          },
        },
      });
    },
    formatAmount(amount, currency) {
      if (amount === null || amount === undefined || amount === '') {
        return '';
      }

      const parsedAmount = Number(
        typeof amount === 'string'
          ? amount.replace(',', '.').replace(/[^\d.-]/g, '')
          : amount
      );

      const curr = currency || 'EUR';
      const locale = this.getLanguage || 'es';

      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: curr,
        minimumFractionDigits: 2,
      }).format(parsedAmount);
    },
    async viewAtc(e, { onBeforeNavigateToDetails } = {}) {
      this.loaded = false;
      this.setLastRouteHome(false);
      this.setLastRouteGeotoolboxSamples(false);

      try {
        this.setIssuedInvoiceSelected(e.row.data);
        await this.fetchIssuedInvoiceDetails(
          e.row.data.CodFactura
        );
        onBeforeNavigateToDetails?.(e.row.data);
        this.$f7router.navigate(
          {
            name: 'issuedInvoicesDetails',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );
        return { opened: true };
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(error)
            ),
            width: 550,
          },
          'error'
        );
        return { opened: false, error };
      } finally {
        this.loaded = true;
      }
    },
    async openTourIssuedInvoiceDetail(options = {}) {
      const firstIssuedInvoice = Array.isArray(this.store)
        ? this.store.find((item) => item?.CodFactura)
        : null;

      if (!firstIssuedInvoice) {
        this.setIssuedInvoiceSelected({});
        this.setIssuedInvoiceDetails({
          CodFactura: '',
          FechaFactura: '',
          OrdenCompra: '',
          ImporteBase: 0,
          ImporteImpuestos: 0,
          ImporteTotal: 0,
          Divisa: this.currency || 'EUR',
          Estado: '',
          LineasPorArticulo: [],
        });
        options.onBeforeNavigateToDetails?.();
        this.$f7router.navigate(
          {
            name: 'issuedInvoicesDetails',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );
        return { opened: true, empty: true };
      }

      return this.viewAtc(
        {
          row: { data: firstIssuedInvoice },
        },
        options
      );
    },
    async download(e) {
      if (e && e.event) {
        e.event.preventDefault?.();
        e.event.stopPropagation?.();
      }

      const codFactura = e?.row?.data?.CodFactura;
      if (!codFactura) return;

      try {
        const b64 = await this.fetchIssuedInvoicePDF(codFactura);
        const clean = b64.includes(',') ? b64.split(',')[1] : b64;

        const byteChars = atob(clean);
        const byteNumbers = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
          byteNumbers[i] = byteChars.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
          type: 'application/pdf',
        });

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${codFactura}.pdf`;
        a.classList.add('external');
        a.rel = 'noopener external';
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (error) {
        this.$notifyDX?.(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage?.(
                error?.message
              ) || 'downloadError'
            ),
            width: 550,
          },
          'error'
        );
        console.error(error);
      }
    },
    ...mapActions('issuedInvoicesModule', [
      'fetchIssuedInvoiceDetails',
      'setIssuedInvoiceSelected',
      'setIssuedInvoiceDetails',
      'fetchIssuedInvoicePDF',
    ]),
    ...mapActions('samples', [
      'setSampleFetchedFromEtl',
      'setLastRouteHome',
      'setLastRouteGeotoolboxSamples',
    ]),
  },
};
</script>

<style>
.summary-icon-invoices {
  width: 35px;
  height: 35px;
  background-image: url('@/static/icons/ic_my_invoices.svg') !important;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

.custom-simple-toolbar.issued-invoices-toolbar .dx-toolbar-before {
  grid-template-columns: 250px 120px 150px 150px;
}

.summary-header-actions {
  display: inline-flex;
  align-items: center;
  margin-left: auto;
}

.issued-invoices-info--header {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--Primary-Blue-70, #00346a);
  cursor: pointer;
}

.custom-grid-summary .dx-toolbar .dx-button[name='IssuedInvoicesPending'],
.custom-grid-summary .dx-toolbar .dx-button[name='IssuedInvoicesPaid'] {
  min-width: 150px !important;
}
</style>
